import { createContext, use, type ReactNode } from "react";
import type { HouseholdRole } from "@finances/shared";
import type { Me } from "./queries.js";

/**
 * Who is signed in, and which household they are looking at.
 *
 * Everything below the router's authenticated boundary can assume both exist,
 * which is why this is a context with a non-null value rather than a query
 * every screen has to handle the loading state of.
 */

export interface Session {
  user: Me["user"];
  householdId: string;
  householdName: string;
  role: HouseholdRole;
  households: Me["households"];
}

const SessionContext = createContext<Session | null>(null);

export function SessionProvider({ value, children }: { value: Session; children: ReactNode }) {
  return <SessionContext value={value}>{children}</SessionContext>;
}

export function useSession(): Session {
  const session = use(SessionContext);
  if (!session) throw new Error("useSession must be used inside a SessionProvider");
  return session;
}

/** Roles are coarse on purpose: a viewer can read everything and change nothing. */
export function useCanEdit(): boolean {
  return useSession().role !== "viewer";
}
